import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  distinctUntilChanged,
  shareReplay,
  take,
} from 'rxjs/operators';
import { User } from '../_models/user';
import { environment } from 'src/environments/environment';
import { Role } from '../_models/role';
import {
  IntercomJWTResponse,
  ValidateUserTokenApiResponse,
} from '../_models/api-responses';
import { httpOptions } from '../_helpers/utils';
import { Laboratory } from '../_models/laboratory';
import { Intercom, shutdown } from '@intercom/messenger-js-sdk';
import { TotpVerifyRequest, RecoveryCodeVerifyRequest } from '../_models/2fa';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly http = inject(HttpClient);

  private isBooted = false;

  // User state
  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);
  public readonly currentUser$ = this.currentUserSubject.asObservable().pipe(
    distinctUntilChanged(
      (prev, curr) =>
        prev?.id === curr?.id &&
        prev?.email === curr?.email &&
        JSON.stringify(prev?.laboratories) ===
          JSON.stringify(curr?.laboratories),
    ),
    shareReplay(1),
  );

  // Org & Lab states
  private readonly selectedLabSubject = new BehaviorSubject<Laboratory | null>(
    null,
  );
  public readonly selectedLab$ = this.selectedLabSubject.asObservable().pipe(
    distinctUntilChanged(
      (prev, curr) => prev?.laboratoryId === curr?.laboratoryId,
    ),
    shareReplay(1),
  );

  private readonly currentOrgSubject = new BehaviorSubject<number | null>(null);
  public readonly currentOrg$ = this.currentOrgSubject
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay(1));

  private readonly userStorage = signal<string | undefined>(undefined);

  httpOptions = httpOptions;

  constructor() {
    // Initialize from storage
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    let cachedUser = localStorage.getItem('currentUser');

    if (cachedUser) {
      try {
        this.userStorage.set('localStorage');
        const user = JSON.parse(cachedUser) as User;
        this.currentUserSubject.next(user);
        this.updateDerivedStates(user);
      } catch {
        localStorage.removeItem('currentUser');
      }
    } else {
      cachedUser = sessionStorage.getItem('currentUser');
      if (cachedUser) {
        try {
          this.userStorage.set('sessionStorage');
          const user = JSON.parse(cachedUser) as User;
          this.currentUserSubject.next(user);
          this.updateDerivedStates(user);
        } catch {
          sessionStorage.removeItem('currentUser');
        }
      }
    }
  }

  private updateDerivedStates(user: User): void {
    const storage =
      this.userStorage() === 'localStorage' ? localStorage : sessionStorage;

    // Update laboratory
    const cachedLab = storage.getItem('currentLab');
    let lab: Laboratory | null = null;
    try {
      lab = JSON.parse(cachedLab) as Laboratory;
    } catch {
      storage.removeItem('currentLab');
    }
    if (lab) {
      this.selectedLabSubject.next(lab);
    } else if (user.laboratories?.length > 0) {
      this.selectedLabSubject.next(user.laboratories[0]);
    }

    // Update organization
    const cachedOrg = sessionStorage.getItem('currentOrgId');
    this.currentOrgSubject.next(
      parseInt(cachedOrg) ? parseInt(cachedOrg) : user.organisationId,
    );
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  public get selectedLaboratory(): Laboratory | null {
    return this.selectedLabSubject.value;
  }

  public get currentOrganisation(): number | null {
    return this.currentOrgSubject.value;
  }

  public patchCurrentUser(patch: Partial<User>): void {
    const current = this.currentUserSubject.value;
    if (!current) return;
    const updated = Object.assign(Object.create(Object.getPrototypeOf(current) as object) as User, current, patch);
    this.currentUserSubject.next(updated);
    const storage = this.userStorage() === 'localStorage' ? localStorage : sessionStorage;
    storage.setItem('currentUser', JSON.stringify(updated));
  }

  public updateCurrentUser(user: User): void {
    this.currentUserSubject.next(user);
    this.updateDerivedStates(user);

    const storage =
      this.userStorage() === 'localStorage' ? localStorage : sessionStorage;
    storage.setItem('currentUser', JSON.stringify(user));
  }

  public setSelectedLaboratory(lab: Laboratory): void {
    const storage =
      this.userStorage() === 'localStorage' ? localStorage : sessionStorage;
    storage.setItem('currentLab', JSON.stringify(lab));
    this.selectedLabSubject.next(lab);
  }

  public setCurrentOrg(id: number): void {
    const storage =
      this.userStorage() === 'localStorage' ? localStorage : sessionStorage;
    storage.setItem('currentOrgId', id.toString());
    this.currentOrgSubject.next(id);
  }

  login(
    username: string,
    password: string,
    rememberMe: boolean,
  ): Observable<boolean> {
    const userLogin = JSON.stringify({
      username,
      password,
      rememberMe,
    });

    return this.http
      .post<User>(`${environment.apiUrl}/login`, userLogin, this.httpOptions)
      .pipe(
        switchMap((response) => {
          const userResponse = new User(
            response.body.email,
            response.body.firstName,
            response.body.lastName,
          );

          userResponse.role = response.body.role;
          userResponse.id = response.body.id;
          userResponse.laboratories = response.body.laboratories;
          userResponse.organisationId = response.body.organisationId;
          userResponse.organisationName = response.body.organisationName;
          userResponse.createdAt = response.body.createdAt;
          userResponse.twoFactorSetupRequired = response.body.twoFactorSetupRequired;
          userResponse.twoFactorEnabled = response.body.twoFactorEnabled;
          userResponse.recoveryCodesRemaining = response.body.recoveryCodesRemaining;

          if (
            userResponse.laboratories.length === 0 &&
            userResponse.role !== Role.OGI_Super_Administrator &&
            userResponse.role !== Role.Super_User
          ) {
            return of(false);
          }

          if (response.body.twoFactorSetupRequired) {
            this.updateCurrentUser(userResponse);
            return of(false);
          }

          this.updateCurrentUser(userResponse);

          return of(true);
        }),
      );
  }

  async bootVisitor() {
    if (this.isBooted) {
      await this.shutdown();
    }
    Intercom({ app_id: environment.intercomAppId });
    this.isBooted = true;
  }

  async bootUser() {
    // 1. Shutdown previous session (visitor or maybe another user)
    await this.shutdown();

    // 2. Wait a tick or guarantee cleanup
    await this.delay(100);

    const storage =
      this.userStorage() === 'localStorage' ? localStorage : sessionStorage;
    const intercomToken = storage.getItem('intercomToken');

    if (!intercomToken) {
      return;
    }

    let decodedPayload: IntercomJWTResponse;
    let id: string;

    decodedPayload = JSON.parse(
      window.atob(intercomToken.split('.')[1]),
    ) as IntercomJWTResponse;
    id = decodedPayload.user_id;

    // 3. Boot logged-in user
    Intercom({
      app_id: environment.intercomAppId,
      intercom_user_jwt: intercomToken,
      user_id: id,
      name:
        this.currentUserValue.firstName +
        ' ' +
        this.currentUserValue.lastName +
        ' [' +
        environment.name +
        ']',
      email: this.currentUserValue.email,
      created_at: this.currentUserValue.createdAt,
      laboratory: this.currentUserValue.laboratories?.[0]?.laboratoryName || '',
      user_role: this.currentUserValue.role,
      organisation: this.currentUserValue.organisationName,
      vertical_padding: 80,
    });
    this.isBooted = true;
  }

  async shutdown() {
    if (!this.isBooted) {
      return;
    }
    shutdown();
    this.isBooted = false;
    // wait a bit to allow localstorage/cookie cleanup
    await this.delay(100);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  authenticateUserIntercom(): Observable<boolean> {
    return this.http
      .get<IntercomJWTResponse>(
        `${environment.apiUrl}/intercom/jwt`,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          const token = response.body.userHash;

          const storage =
            this.userStorage() === 'localStorage'
              ? localStorage
              : sessionStorage;
          storage.setItem('intercomToken', token);

          return true;
        }),
      );
  }

  async logout(): Promise<void> {
    // Fire-and-forget — don't block local logout on server response,
    // but the request must actually be sent.
    this.http
      .post<unknown>(`${environment.apiUrl}/logout`, this.httpOptions)
      .pipe(
        take(1),
        catchError(() => EMPTY),
      )
      .subscribe();

    const storage =
      this.userStorage() === 'localStorage' ? localStorage : sessionStorage;
    storage.removeItem('currentUser');
    storage.removeItem('labList');
    storage.removeItem('allLabs');
    // Do not remove current lab/org if OGI_Super_Administrator
    // Need to retain values if they log back in
    if (this.currentUserValue?.role !== Role.OGI_Super_Administrator) {
      storage.removeItem('currentLab');
      storage.removeItem('currentOrgId');
    }
    storage.removeItem('intercomToken');

    await this.shutdown();

    this.currentUserSubject.next(null);
    this.selectedLabSubject.next(null);
    this.currentOrgSubject.next(null);
  }

  forgotPassword(email: string): Observable<void> {
    const forgotPasswordObject = JSON.stringify({
      username: email,
    });
    return this.http
      .post<string>(
        `${environment.apiUrl}/account/forgot-password`,
        forgotPasswordObject,
        this.httpOptions,
      )
      .pipe(map(() => void 0));
  }

  validateResetToken(
    resetToken: string,
    resetEmail: string,
  ): Observable<boolean> {
    const validateTokenObject = JSON.stringify({
      token: resetToken,
      email: resetEmail,
    });
    return this.http
      .post<boolean>(
        `${environment.apiUrl}/account/validate-reset-token`,
        validateTokenObject,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  resetPassword(
    token: string,
    password: string,
    email: string,
  ): Observable<boolean> {
    const resetObject = { password, token, email };
    return this.http
      .post<boolean>(
        `${environment.apiUrl}/account/reset-password`,
        resetObject,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  validateUserToken(token: string): Observable<User> {
    return this.http
      .get<ValidateUserTokenApiResponse>(
        `${environment.apiUrl}/users/invite/${token}`,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          const { invitation } = response.body;
          const userResponse = new User(
            invitation.email,
            invitation.firstName,
            invitation.lastName,
            null,
            invitation.id,
          );
          return userResponse;
        }),
      );
  }

  verifyTwoFactorCode(request: TotpVerifyRequest): Observable<void> {
    return this.http
      .post<User>(
        `${environment.apiUrl}/login/2fa`,
        request,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          this.userStorage.set(
            request.rememberMe ? 'localStorage' : 'sessionStorage',
          );
          const userResponse = new User(
            response.body.email,
            response.body.firstName,
            response.body.lastName,
          );
          userResponse.role = response.body.role;
          userResponse.id = response.body.id;
          userResponse.laboratories = response.body.laboratories;
          userResponse.organisationId = response.body.organisationId;
          userResponse.organisationName = response.body.organisationName;
          userResponse.createdAt = response.body.createdAt;
          userResponse.twoFactorSetupRequired = response.body.twoFactorSetupRequired;
          userResponse.twoFactorEnabled = response.body.twoFactorEnabled;
          userResponse.recoveryCodesRemaining = response.body.recoveryCodesRemaining;

          this.updateCurrentUser(userResponse);
        }),
      );
  }

  verifyTwoFactorRecoveryCode(request: RecoveryCodeVerifyRequest): Observable<void> {
    return this.http
      .post<User>(
        `${environment.apiUrl}/login/2fa/recovery`,
        request,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          this.userStorage.set('sessionStorage');
          const userResponse = new User(
            response.body.email,
            response.body.firstName,
            response.body.lastName,
          );
          userResponse.role = response.body.role;
          userResponse.id = response.body.id;
          userResponse.laboratories = response.body.laboratories;
          userResponse.organisationId = response.body.organisationId;
          userResponse.organisationName = response.body.organisationName;
          userResponse.createdAt = response.body.createdAt;
          userResponse.twoFactorSetupRequired = response.body.twoFactorSetupRequired;
          userResponse.twoFactorEnabled = response.body.twoFactorEnabled;
          userResponse.recoveryCodesRemaining = response.body.recoveryCodesRemaining;

          this.updateCurrentUser(userResponse);
        }),
      );
  }

  createAccount(user: User, password: string): Observable<boolean> {
    const url = `${environment.apiUrl}/users?invitationToken=${user.token}`;
    const createUserObject = JSON.stringify({
      password: password,
    });
    return this.http
      .post<boolean>(url, createUserObject, {
        ...this.httpOptions,
        observe: 'response',
      })
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }
}
